/**
 * ConsentLevel - Displays and manages wallet consent levels
 *
 * Shows what data the user has shared and allows requesting additional access.
 * Follows Pentatonic design system.
 */

import React, { useState } from "react";
import { Shield, ChevronRight, Check, Unlock, X } from "lucide-react";

import { useWallet } from "../../contexts/WalletContext";
import { Button } from "../ui/button";

// Consent scopes and their descriptions
const CONSENT_SCOPES = {
  profile: {
    label: "Basic Profile",
    description: "Name, email, and display preferences",
    icon: "user",
    level: 1,
  },
  addresses: {
    label: "Shipping Addresses",
    description: "Your saved delivery addresses",
    icon: "map-pin",
    level: 2,
  },
  sizes: {
    label: "Size Preferences",
    description: "Clothing and shoe sizes",
    icon: "ruler",
    level: 2,
  },
  payment_methods: {
    label: "Payment Methods",
    description: "Saved cards for quick checkout",
    icon: "credit-card",
    level: 3,
  },
  purchase: {
    label: "Purchase History",
    description: "Previous orders for recommendations",
    icon: "shopping-bag",
    level: 3,
  },
  preferences: {
    label: "Style Preferences",
    description: "Brands, colors, and style choices",
    icon: "sparkles",
    level: 2,
  },
  digital_twin: {
    label: "AI Insights",
    description: "AI-learned preferences from your activity",
    icon: "brain",
    level: 3,
  },
};

// Default scopes requested during connection
const DEFAULT_SCOPES = ["profile", "addresses", "payment_methods", "sizes", "purchase"];

/**
 * Compact indicator showing current consent level
 */
export function ConsentLevelIndicator({ className = "" }) {
  const { isConnected } = useWallet();

  if (!isConnected) {
    return null;
  }

  // Count active scopes
  const activeScopes = DEFAULT_SCOPES.filter((scope) => CONSENT_SCOPES[scope]);
  const maxLevel = Math.max(...activeScopes.map((s) => CONSENT_SCOPES[s].level));

  const levelLabels = {
    1: "Basic",
    2: "Standard",
    3: "Full",
  };

  return (
    <div className={`inline-flex items-center gap-1.5 text-xs text-penta-grey-medium ${className}`}>
      <Shield size={12} className="text-penta-mint" />
      <span className="font-light">{levelLabels[maxLevel]} access</span>
    </div>
  );
}

/**
 * Detailed consent scope display
 */
export function ConsentScopeList({ className = "" }) {
  const { isConnected } = useWallet();

  if (!isConnected) {
    return null;
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Shield size={16} className="text-penta-mint" />
        <span className="text-sm font-light text-penta-black">Data you&apos;re sharing</span>
      </div>
      <div className="space-y-1">
        {DEFAULT_SCOPES.map((scope) => {
          const config = CONSENT_SCOPES[scope];
          if (!config) return null;

          return (
            <div
              key={scope}
              className="flex items-center justify-between py-1.5 px-2 bg-penta-grey-light/50"
            >
              <div className="flex items-center gap-2">
                <Check size={12} className="text-penta-mint" />
                <span className="text-xs text-penta-black font-light">{config.label}</span>
              </div>
              <span className="text-[10px] text-penta-grey-medium">{config.description}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Modal for consent escalation - request additional permissions
 */
export function ConsentEscalationModal({ isOpen, onClose, onEscalate }) {
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Additional scopes that can be requested
  const additionalScopes = Object.entries(CONSENT_SCOPES)
    .filter(([key]) => !DEFAULT_SCOPES.includes(key))
    .map(([key, config]) => ({ key, ...config }));

  const handleToggleScope = (scope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleEscalate = async () => {
    if (selectedScopes.length === 0) return;

    setIsLoading(true);
    try {
      if (onEscalate) {
        await onEscalate(selectedScopes);
      }
      onClose();
    } catch (err) {
      console.error("Error escalating consent:", err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-penta-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white border border-neutral-200 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <Unlock size={18} className="text-penta-mint" />
            <h2 className="text-lg font-light text-penta-black">Request Additional Access</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-penta-grey-medium hover:text-penta-black transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-penta-grey-medium font-light mb-4">
            Select additional data to share for better personalization. Your wallet will ask
            for approval.
          </p>

          {additionalScopes.length === 0 ? (
            <div className="text-center py-4 text-penta-grey-medium text-sm">
              You&apos;ve already granted full access.
            </div>
          ) : (
            <div className="space-y-2">
              {additionalScopes.map(({ key, label, description }) => {
                const isSelected = selectedScopes.includes(key);
                return (
                  <button
                    key={key}
                    onClick={() => handleToggleScope(key)}
                    className={`w-full flex items-center justify-between p-3 text-left transition-all duration-200 ${
                      isSelected
                        ? "bg-penta-mint/10 border border-penta-mint"
                        : "bg-penta-grey-light border border-transparent hover:border-penta-grey-medium"
                    }`}
                  >
                    <div>
                      <div className="text-sm font-light text-penta-black">{label}</div>
                      <div className="text-xs text-penta-grey-medium">{description}</div>
                    </div>
                    <div
                      className={`w-5 h-5 flex items-center justify-center ${
                        isSelected ? "bg-penta-mint text-white" : "bg-white border border-neutral-300"
                      }`}
                    >
                      {isSelected && <Check size={12} />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-neutral-200">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleEscalate}
            disabled={selectedScopes.length === 0 || isLoading}
            className="flex-1"
          >
            {isLoading ? "Requesting..." : "Request Access"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact button to open consent escalation
 */
export function ConsentEscalationButton({ className = "" }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isConnected, connectWallet } = useWallet();

  if (!isConnected) {
    return null;
  }

  const handleEscalate = async (newScopes) => {
    // Re-connect with additional scopes
    // This would trigger the wallet popup asking for additional permissions
    console.log("Requesting additional scopes:", newScopes);
    // For now, this would need backend support to handle scope escalation
    // The wallet connect popup would show what additional access is being requested
    await connectWallet();
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={`inline-flex items-center gap-1 text-xs text-penta-blue hover:underline transition-all duration-200 ${className}`}
      >
        <span className="font-light">Manage data sharing</span>
        <ChevronRight size={12} />
      </button>

      <ConsentEscalationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onEscalate={handleEscalate}
      />
    </>
  );
}

// Default export
export default ConsentLevelIndicator;
